import { useTranslation } from "react-i18next";

import type { WebhookRegistration } from "../api";
import { hasWebhookError } from "../utils/getWebhookErrorStatus";

import Icons from "@/components/icons";
import { Badge } from "@/components/ui/v2/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";

interface WebhookRegistrationContentProps {
  registration: WebhookRegistration;
  onDeleteButton: React.ReactNode;
  webhookIdClassName?: string;
}

export function WebhookRegistrationContent({
  registration,
  onDeleteButton,
  webhookIdClassName = "font-mono text-sm",
}: WebhookRegistrationContentProps) {
  const { t } = useTranslation();
  const hasError = hasWebhookError(registration);
  const hasMismatch = registration.isMismatched ?? false;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <label className="text-sm font-medium text-muted-foreground">
            {t("webhooks.recruitnow.webhookId")}
          </label>
          <div className="mt-1">
            {registration.id ? (
              <span className={`${webhookIdClassName} truncate block`}>
                {registration.id}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">
                {t("common.none")}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasError ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="warning" size="medium">
                    <Icons.WarningIcon fontSize={16} />
                    {t("common.error")}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="flex flex-col gap-1">
                  {registration.errorState && (
                    <div>
                      <span className="font-semibold">{t("webhooks.recruitnow.errorState")}: </span>
                      <span>{registration.errorState}</span>
                    </div>
                  )}
                  {registration.errorMessage && (
                    <div>
                      <span className="font-semibold">{t("webhooks.recruitnow.errorMessage")}: </span>
                      <span>{registration.errorMessage}</span>
                    </div>
                  )}
                  {registration.isSecretMismatched && (
                    <div>{t("webhooks.recruitnow.secretMismatch")}</div>
                  )}
                  {registration.isUriMismatched && (
                    <div>{t("webhooks.recruitnow.uriMismatch")}</div>
                  )}
                  {registration.isFiltersMismatched && (
                    <div>{t("webhooks.recruitnow.filtersMismatch")}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : hasMismatch ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="warning" size="medium">
                    <Icons.WarningIcon fontSize={16} />
                    {t("webhooks.recruitnow.status.mismatch")}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="flex flex-col gap-1">
                  {registration.isSecretMismatched && (
                    <div>{t("webhooks.recruitnow.secretMismatch")}</div>
                  )}
                  {registration.isUriMismatched && (
                    <div>{t("webhooks.recruitnow.uriMismatch")}</div>
                  )}
                  {registration.isFiltersMismatched && (
                    <div>{t("webhooks.recruitnow.filtersMismatch")}</div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Badge variant="success" size="medium">
              <Icons.CheckCircleIcon fontSize={16} />
              {t("common.ok")}
            </Badge>
          )}
          {registration.id && onDeleteButton}
        </div>
      </div>
      <div className="space-y-2">
        {registration.webHookUri && (
          <div className="min-w-0">
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.webhookUri")}
            </label>
            <div className="mt-1 min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm truncate block cursor-help">
                    {registration.webHookUri}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs break-all">{registration.webHookUri}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        {registration.secret && (
          <div className="min-w-0">
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.webhookSecret")}
            </label>
            <div className="mt-1 min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-mono text-sm truncate block cursor-help">
                    {registration.secret}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs break-all">{registration.secret}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        {registration.faultNotificationEmail && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.faultNotificationEmail")}
            </label>
            <div className="mt-1">
              <span className="text-sm">
                {registration.faultNotificationEmail}
              </span>
            </div>
          </div>
        )}
        {registration.errorState && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.errorState")}
            </label>
            <div className="mt-1">
              <span className="text-sm">{registration.errorState}</span>
            </div>
          </div>
        )}
        {registration.errorMessage && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.errorMessage")}
            </label>
            <div className="mt-1">
              <span className="text-sm">{registration.errorMessage}</span>
            </div>
          </div>
        )}
        {registration.filters && registration.filters.length > 0 && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              {t("webhooks.recruitnow.filters")}
            </label>
            <div className="mt-1">
              <div className="flex flex-wrap gap-1">
                {registration.filters.map((filter, filterIndex) => (
                  <Badge
                    key={filterIndex}
                    variant="outline"
                    size="small"
                    className="text-sm"
                  >
                    {filter}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
